import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useIntegrations } from "../../../hooks/useIntegrations";
import IntegrationTable from "../../../components/IntegrationTable";
import AddIntegrationForm from "../../../components/AddIntegrationForm";
import { useTranslation } from "react-i18next";
// (mantido aqui para futuras regras por tipo)
import { fetchData } from "../../../api/client";
import {
  buildPassagensDeletePuts,
  passagensByKeyFromConfigurationsResponse,
  passagensPartnerConfiguredInByKey,
  getPassagensVirtualStatus,
  mergePassagensUiAtivoMap,
  PASSAGENS_UI_ATIVO_KEY,
} from "../../../constants/passagensConfigHelpers";

const Integrations = () => {
  const { t } = useTranslation("settings");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [passagensConfig, setPassagensConfig] = useState({});
  const [passagensConfigLoading, setPassagensConfigLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    testMutation,
    isLoading,
  } = useIntegrations(1, 500, search);

  useEffect(() => {
    if (error) setErrorMessage(t("integrationsPage.loadError"));
    else setErrorMessage(null);
  }, [error, t]);

  const loadPassagensConfig = useCallback(async () => {
    try {
      setPassagensConfigLoading(true);
      const res = await fetchData("/configurations", "GET");
      setPassagensConfig(passagensByKeyFromConfigurationsResponse(res));
    } catch (_) {
      setPassagensConfig({});
    } finally {
      setPassagensConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPassagensConfig();
  }, [loadPassagensConfig]);

  /* ===== Handlers Linha ===== */
  const handleEdit = (integration) => {
    setSelectedIntegration(integration);
    setErrorMessage(null);
  };

  const handleDelete = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteIntegration = () => {
    if (!confirmDelete) return;
    const idStr = String(confirmDelete);
    if (idStr.startsWith("passagens:")) {
      const partner = idStr.slice("passagens:".length);
      setIsProcessing(true);
      setErrorMessage(null);
      (async () => {
        try {
          const puts = buildPassagensDeletePuts(passagensConfig, partner);
          await Promise.all(
            puts.map(([key, value]) =>
              fetchData(`/configurations/${encodeURIComponent(key)}`, "PUT", { value })
            )
          );
          setConfirmDelete(null);
          await loadPassagensConfig();
        } catch (err) {
          setErrorMessage(
            err?.response?.data?.message || err?.message || "Erro ao excluir configuração de passagens."
          );
        } finally {
          setIsProcessing(false);
        }
      })();
      return;
    }
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err?.response?.data?.message || "Erro ao excluir a integração.");
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  const toggleStatus = (row) => {
    setIsProcessing(true);
    updateMutation.mutate(
      { id: row.id, status: row.status ? 0 : 1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao alterar status da integração."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const togglePassagensVirtual = async (row) => {
    const partner = row?.provedor;
    if (!partner) return;
    const cur = Number(row?.status) === 1;
    const next = !cur;
    setIsProcessing(true);
    try {
      const map = mergePassagensUiAtivoMap(passagensConfig, partner, next);
      const value = JSON.stringify(map);
      await fetchData(
        `/configurations/${encodeURIComponent(PASSAGENS_UI_ATIVO_KEY)}`,
        "PUT",
        { value }
      );
      setPassagensConfig((prev) => ({ ...prev, [PASSAGENS_UI_ATIVO_KEY]: value }));
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao alterar status da integração.");
    } finally {
      setIsProcessing(false);
    }
  };

  /* ===== Ações em massa ===== */
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao ativar integrações.");
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao desativar integrações.");
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao excluir integrações.");
    } finally {
      setIsProcessing(false);
    }
  };

  /* ===== Paginação/Busca/Form ===== */
  const handlePageChange = (newPage) => setPage(newPage);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => {
    setSearch(term);
    setPage(0);
    refetch();
  };
  const handleCloseForm = () => {
    setSelectedIntegration(null);
    setErrorMessage(null);
  };

  const passagensVirtualRows = useMemo(() => {
    const cfg = passagensConfig || {};
    const partners = [
      ["totalbus", "Passagens - RJ (Totalbus)"],
      ["praxxio", "Passagens - Praxio (Luna)"],
      ["rodosoft", "Passagens - Rodosoft"],
      ["distribusion", "Passagens - Distribusion"],
      ["rodoviariaonline", "Passagens - Rodoviária Online"],
      ["gopass", "Passagens - Gopass"],
      ["buson", "Passagens - Buson"],
    ];
    return partners
      .filter(([key]) => passagensPartnerConfiguredInByKey(key, cfg))
      .map(([provedor, nome]) => ({
        id: `passagens:${provedor}`,
        __virtual: true,
        provedor,
        nome,
        status: getPassagensVirtualStatus(provedor, cfg),
      }));
  }, [passagensConfig]);

  const allRowsForTable = useMemo(() => {
    const apiRows = Array.isArray(data?.integrations) ? data.integrations : [];
    const combined = [...passagensVirtualRows, ...apiRows];

    const q = String(search || "").trim().toLowerCase();
    if (!q) return combined;
    return combined.filter((r) => {
      const hay = `${r?.nome || ""} ${r?.provedor || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data?.integrations, passagensVirtualRows, search]);

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("integrationsPage.title")}</Typography>
        {!selectedIntegration && (
          <Button
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            onClick={() => setSelectedIntegration({})}
          >
            {t("integrationsPage.addIntegration")}
          </Button>
        )}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
            <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      )}

      {selectedIntegration ? (
        <AddIntegrationForm
          data={selectedIntegration}
          isLoading={isLoading || isProcessing}
          onPassagensSaved={loadPassagensConfig}
          onClose={handleCloseForm}
          onSubmit={(payload) => {
            setIsProcessing(true);
            updateMutation.mutate(payload, {
              onSuccess: () => { refetch(); handleCloseForm(); },
              onError: (err) =>
                setErrorMessage(err?.response?.data?.message || "Erro ao salvar a integração."),
              onSettled: () => setIsProcessing(false),
            });
          }}
          onTest={async (payloadOrId) => {
            try {
              setIsProcessing(true);
              // se ainda não existe, primeiro cria/atualiza
              let id = payloadOrId?.id;
              if (!id && payloadOrId) {
                const res = await updateMutation.mutateAsync(payloadOrId);
                id = res?.data?.id || res?.id;
              }
              if (!id) throw new Error("Integração sem ID para testar.");
              await testMutation.mutateAsync(id);
            } catch (err) {
              setErrorMessage(err?.response?.data?.message || err?.message || "Falha ao testar integração.");
            } finally {
              setIsProcessing(false);
            }
          }}
        />
      ) : (
        <>
          {selectedIds?.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => bulkActivate(selectedIds)}
                disabled={isLoading || isProcessing}
              >
                Ativar
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => bulkDeactivate(selectedIds)}
                disabled={isLoading || isProcessing}
              >
                Desativar
              </Button>
            </Box>
          )}
          <IntegrationTable
            integrations={allRowsForTable}
            rowCount={allRowsForTable.length}
            pageSize={pageSize}
            page={page}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            isLoading={isLoading || isProcessing || passagensConfigLoading}
            paginationMode="client"
            onEdit={(row) => {
              if (String(row?.id || "").startsWith("passagens:")) {
                handleEdit(row);
                return;
              }
              handleEdit(row);
            }}
            onDelete={handleDelete}
            onToggleStatus={(row) => {
              if (String(row?.id || "").startsWith("passagens:")) {
                togglePassagensVirtual(row);
                return;
              }
              toggleStatus(row);
            }}
            onDeleteSelected={bulkDelete}
            onSelectionChange={setSelectedIds}
          />
        </>
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("shared.deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("shared.processingDelete") : t("integrationsPage.deleteIntegrationQuestion")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteIntegration}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Integrations;
